import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './authContext';
import { auth } from '../firebase';
import { GoogleAuthProvider, signInWithPopup, OAuthProvider, signInWithEmailAndPassword } from 'firebase/auth';
import { Box, Typography, Button, CircularProgress, Alert, TextField } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { post } from './api';

const apiUrl = process.env.REACT_APP_API_URL ||
  (process.env.REACT_APP_PROJECT_ID === 'shopik-production'
    ? 'https://us-central1-shopik-production.cloudfunctions.net/api_handler/api/'
    : 'https://us-central1-shopik-stage.cloudfunctions.net/api_handler/api/');

// Matches client_id in shopify.app.toml — used to deep-link back into the embedded
// app in Shopify admin once linking completes.
const SHOPIFY_CLIENT_ID = 'a3aad10ce98345c3c85f0e094f510287';

const PageShell = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: 2,
      textAlign: 'center',
      px: 3,
    }}
  >
    <img src="/images/logo.png" loading="lazy" alt="Hemlock" style={{ maxWidth: '160px', marginBottom: 8 }} />
    {children}
  </Box>
);

const LinkShopify = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const { user, customer, loading } = useAuth();
  const navigate = useNavigate();

  const linkToken = searchParams.get('token');
  const shopDomain = searchParams.get('shop');

  useEffect(() => {
    if (linkToken) {
      window.localStorage.setItem('shopifyLinkToken', linkToken);
      if (shopDomain) {
        window.localStorage.setItem('shopifyShopDomain', shopDomain);
      }
    }
  }, [linkToken, shopDomain]);

  useEffect(() => {
    const storedToken = linkToken || window.localStorage.getItem('shopifyLinkToken');
    if (!loading && customer && customer.apiKey && storedToken && !linking && !linkSuccess) {
      performLink(storedToken, customer.apiKey);
    }
  }, [loading, customer, linkToken, linking, linkSuccess]);

  const performLink = async (token, apiKey) => {
    setLinking(true);
    setLinkError(null);
    try {
      const response = await fetch(`${apiUrl}shopify-app/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ linkToken: token }),
      });
      const result = await response.json();
      if (response.ok && result.status === 'linked') {
        setLinkSuccess(true);
        const returnShop = shopDomain || window.localStorage.getItem('shopifyShopDomain');
        window.localStorage.removeItem('shopifyLinkToken');
        window.localStorage.removeItem('shopifyShopDomain');
        // Send the merchant back into Shopify admin — this tab is a standalone browser
        // tab (opened via email link or an `external` button), not the embedded app
        // iframe, so we can't just navigate() back into it.
        setTimeout(() => {
          if (returnShop) {
            window.location.href = `https://${returnShop}/admin/apps/${SHOPIFY_CLIENT_ID}`;
          } else {
            navigate('/dashboard');
          }
        }, 2000);
      } else {
        setLinkError(result.error || 'Failed to link Shopify store. Please try again.');
      }
    } catch (error) {
      console.error('Link error:', error);
      setLinkError('Network error. Please check your connection and try again.');
    } finally {
      setLinking(false);
    }
  };

  const signInWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider).catch(() => setLinkError('Google sign-in failed. Please try again.'));
  };

  const signInWithApple = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    signInWithPopup(auth, provider).catch(() => setLinkError('Apple sign-in failed. Please try again.'));
  };

  const signInWithPassword = async () => {
    setLinkError(null);
    if (!email || !password) {
      setLinkError('Please enter your email and password.');
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in:', error);
      setLinkError('Incorrect email or password. Please try again.');
    }
  };

  if (loading || linking || (user && customer && customer.apiKey)) {
    return (
      <PageShell>
        <CircularProgress sx={{ color: '#4751ed', mt: 1 }} />
        <Typography variant="body1" color="text.secondary">
          {linking ? 'Connecting your Shopify store…' : 'Loading…'}
        </Typography>
      </PageShell>
    );
  }

  if (linkSuccess) {
    return (
      <PageShell>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#4751ed' }} />
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Shopify store connected
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Redirecting you back to Shopify…
        </Typography>
      </PageShell>
    );
  }

  if (!linkToken && !window.localStorage.getItem('shopifyLinkToken')) {
    return (
      <PageShell>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Invalid link. Please install the Hemlock app from the Shopify App Store.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ backgroundColor: '#4751ed', textTransform: 'none', ':hover': { backgroundColor: '#6133aa' } }}
        >
          Go to Login
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Typography variant="h5" sx={{ fontWeight: 500 }}>
        Connect Your Shopify Store
      </Typography>

      {shopDomain && (
        <Typography variant="body2" color="text.secondary">
          Store: <strong>{shopDomain}</strong>
        </Typography>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360 }}>
        Sign in to your Hemlock account to connect your Shopify store.{' '}
        <Typography component="span" variant="body2" color="text.secondary">
          Don't have an account? Create one by signing in below.
        </Typography>
      </Typography>

      {linkError && (
        <Alert severity="error" sx={{ maxWidth: 400, width: '100%' }}>
          {linkError}
        </Alert>
      )}

      <button className="signin-button" onClick={signInWithGoogle}>
        <img src="/images/google-signin.png" alt="Google Sign in" />
      </button>
      <button className="signin-button" onClick={signInWithApple}>
        <img src="/images/apple-signin.png" alt="Apple Sign in" />
      </button>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', width: '100%', maxWidth: 260 }}>
        <TextField
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          fullWidth
        />
        <Button
          variant="contained"
          fullWidth
          sx={{ backgroundColor: '#4751ed', textTransform: 'none', ':hover': { backgroundColor: '#6133aa' } }}
          onClick={signInWithPassword}
        >
          Sign In
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mt: 1 }}>
        After signing in, your Shopify store will be automatically connected to your Hemlock account.
      </Typography>
    </PageShell>
  );
};

export default LinkShopify;
